import React from 'react'

// Components
import { LoadingPlaceholder, Modal, Flex, Banner } from '@sketch/components'
import {
  SummaryError,
  SummaryHeader,
  SummaryLine,
  SummaryPaymentMethod,
  SummaryTotalDescribed,
  SummaryTotalLine,
  SummaryDiscountLine,
  SummaryProrated,
} from '../../components/Summary'

// Types
import {
  BillingPlanFragment,
  BillingSeatsInfoFragment,
  PaymentDetailsFragment,
  useSubscriptionUpdateBillingSimulationQuery,
} from '@sketch/gql-types'

// Utils
import {
  getAppliedDiscount,
  getPaymentTypeByProratedAmounts,
} from 'modules/workspace/utils'

interface MonthlyToYearlyContentProps {
  selectedPlan: BillingPlanFragment
  customerId: string
  seats: BillingSeatsInfoFragment
  paymentDetails?: PaymentDetailsFragment
}

export const MonthlyToYearlyContent: React.FC<MonthlyToYearlyContentProps> = ({
  selectedPlan,
  customerId,
  seats,
  paymentDetails,
}) => {
  const {
    data,
    loading,
    refetch,
    error,
  } = useSubscriptionUpdateBillingSimulationQuery({
    variables: {
      customerId,
      planId: selectedPlan.id,
      seats: seats.currentSeatsTotal,
    },
  })

  if (loading) {
    return (
      <Flex height={248} w="100%" justifyContent="center" alignItems="center">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  const { prorated, couponInfo, totalAmount } =
    data?.subscriptionUpdateBillingSimulation || {}
  const paymentType = getPaymentTypeByProratedAmounts(
    prorated?.chargeAmount,
    prorated?.creditsAmount
  )
  const appliedDiscount = getAppliedDiscount(couponInfo ?? undefined)

  return (
    <Modal.Body>
      {error ? (
        <SummaryError refetch={() => refetch()} />
      ) : (
        <>
          <SummaryHeader
            plan={selectedPlan}
            paymentMethod={
              // Wait for the BE response to show the proper payment method
              prorated && (
                <SummaryPaymentMethod
                  chargeMethod={paymentDetails!}
                  paymentType={paymentType}
                />
              )
            }
          />
          <SummaryLine
            description={`${seats.currentSeatsTotal} Seats`}
            value={totalAmount}
            loading={loading}
          />
          {appliedDiscount && (
            <SummaryDiscountLine
              appliedDiscount={appliedDiscount}
              isAlreadyApplied={!!couponInfo}
            />
          )}
          {prorated && (
            <SummaryProrated
              daysElapsedInCycle={prorated.daysRemainingInCycle}
              refundAmountForNewSeats={prorated.discountForUnusedDays}
            />
          )}
          <SummaryTotalLine
            description="Total due"
            loading={loading}
            value={prorated?.amount || 0}
          />
          <SummaryTotalDescribed
            charge={prorated?.chargeAmount}
            credit={prorated?.creditsAmount}
          />
          {!!seats.scheduledSeatsTotal && (
            <Banner type="warning" showIcon={false}>
              <strong>Your scheduled seat changes will be cancelled</strong>
              <br />
              Please review your scheduled seat changes and take action prior to
              changing your plan if needed. Adding seats will remain an option
              after the plan change by using the <strong>Add Seats</strong>{' '}
              option available in the <strong>People</strong> tab.
            </Banner>
          )}
        </>
      )}
    </Modal.Body>
  )
}
